import React from 'react';
import styled from 'styled-components';
import { LineStats } from './Types/lineStats';
import { TimeContainer } from '../Elements/TimeComponents'
import { colors } from "../../utils/colors";
import * as TypographyStyles from '../TypographyStyles';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';


const ContainerCard = styled('div')`
    display: flex;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: stretch;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    background: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: ${slateColors[300]};
    }
`

const CardHeader = styled('div')`
    display: flex;
    align-items: flex-start;
    gap: 4px;
`

const AvgDelayTable = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    align-self: stretch;
`

const StationItem = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
`

const DataRow = styled('div')`
    display: flex;
    align-items: center;
    gap: 16px;
    align-self: stretch;
`

const TimeIndication = styled('div')`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 200px;
`

const ColourScale = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
`

const ColorBar = styled.div<{ $maxDelay: number }>`
  position: relative;
  width: 100%;
  height: 6px;
  background: ${props => {
    const { $maxDelay: maxDelay } = props;
    
    // Calculate positions based on maxDelay
    const color1Position = 0; // Start of the gradient
    const color2Position = Math.min(600 / maxDelay * 100, 100);
    const color3Position = Math.min(1200 / maxDelay * 100, 100);
    const color4Position = Math.min(3600 / maxDelay * 100, 100);
    
    return `linear-gradient(to right, 
      #52FF00 ${color1Position}%, 
      #FCF200 ${color2Position}%, 
      #F00 ${color3Position}%, 
      #AF0000 ${color4Position}%)`;
  }};
  border-radius: 4px;
`;

const Dot = styled.div<{ $position: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$position}%;
  width: 6px;
  height: 6px;
  background-color: ${slateColors[900]};
  border: 2px solid white;
  border-radius: 50%;
  transform: translate(-50%, -50%);
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${slateColors[200]};
  width: 100%;
  margin: 0;
`;


// Define an interface representing the expected structure of the data prop
interface AvgDelayCardProps {
    data: LineStats[]; // Expecting an array of LineStats objects
}

const AvgDelayCard: React.FC<AvgDelayCardProps> = ({ data }) => {
    const { t } = useTranslation();
    
    // Find min and max delays across all stops
    const minDelay = Math.min(
        ...data.flatMap(stop => [stop.avgArrivalDelay!, stop.avgDepartureDelay!]).filter(x => x !== undefined), 0
    );
    const maxDelay = Math.max(
        ...data.flatMap(stop => [stop.avgArrivalDelay!, stop.avgDepartureDelay!]).filter(x => x !== undefined), 0
    );

    return (
        <ContainerCard>
            <CardHeader>
                <TypographyStyles.BodyLSemibold color={colors.black}>{t('statistics.averageDelay')}</TypographyStyles.BodyLSemibold>
                <TypographyStyles.BodyLRegular color={colors.grey600}>{t('statistics.last30Days')}</TypographyStyles.BodyLRegular>
            </CardHeader>
            <AvgDelayTable>
                {data.map((stop, index) => (
                <React.Fragment key={index}>
                    <StationItem>
                        <TypographyStyles.BodyMSemibold color={slateColors[800]}>{stop.stopName ?? '(no name)'}</TypographyStyles.BodyMSemibold>

                        {/* Conditionally render arrival data row if available */}
                        {stop.plannedArrivalTimeDisplay && (
                            <DataRow>
                                <TimeIndication>
                                    <TypographyStyles.BodyMRegular 
                                        color={slateColors[600]} 
                                        style={{ width: '64px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {t('statistics.arrival')}
                                    </TypographyStyles.BodyMRegular>
                                    <TimeContainer 
                                            time={stop.plannedArrivalTimeDisplay}
                                            averageDelaySec={stop.avgArrivalDelay}
                                            weight={400}
                                        />
                                </TimeIndication>
                                <ColourScale>
                                    <ColorBar $maxDelay={maxDelay}>
                                        <Dot 
                                            $position={((stop.avgArrivalDelay! - minDelay) / (maxDelay - minDelay)) * 100}
                                        />
                                    </ColorBar>
                                </ColourScale>
                            </DataRow>
                        )}

                        {/* Conditionally render departure data row if available */}

                        {stop.plannedDepartureTimeDisplay && (
                            <DataRow>
                                <TimeIndication>
                                    <TypographyStyles.BodyMRegular                                    
                                        color={slateColors[600]} 
                                        style={{ width: '64px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {t('statistics.departure')}
                                    </TypographyStyles.BodyMRegular>
                                    <TimeContainer 
                                        time={stop.plannedDepartureTimeDisplay} 
                                        averageDelaySec={stop.avgDepartureDelay}  
                                        weight={400}
                                    />
                                </TimeIndication>
                                <ColourScale>
                                    <ColorBar $maxDelay={maxDelay}>
                                        <Dot 
                                            $position={((stop.avgDepartureDelay! - minDelay) / (maxDelay - minDelay)) * 100}
                                        />
                                    </ColorBar>
                                </ColourScale>
                            </DataRow>
                        )}
                    </StationItem>
                    {index < data.length - 1 && <Divider />} {/* Add divider between items */}
                </React.Fragment>
                ))}
            </AvgDelayTable>
        </ContainerCard>
    );
};

export default AvgDelayCard;
